import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Phone, Menu, X, LogOut, ChevronDown, Apple, Pill, UtensilsCrossed, Plug, Truck, IceCream, Fish, Beef, ShoppingCart, Snowflake } from "lucide-react";
import { PHONE_NUMBER, PHONE_DISPLAY } from "@/lib/constants";
import { trackCTAClick } from "@/lib/analytics";
import { scrollToAnchor } from "@/lib/scrollToAnchor";
import { useDockedBarVisible } from "./BookingDock";
import { useLanguageSwitch } from "./LanguageRoute";

const LEISTUNGEN = [
  { label: "Lebensmittel-Transport", to: "/lebensmittel-transport", icon: Apple },
  { label: "Pharma-Transport", to: "/pharma-transport", icon: Pill },
  { label: "Catering & Events", to: "/catering-events", icon: UtensilsCrossed },
  { label: "Mobile Kühlung", to: "/mobile-kuehlung", icon: Plug },
];


const Header = () => {
  const { t, i18n } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpenState] = useState(false);
  const setIsMobileMenuOpen = (open: boolean) => {
    setIsMobileMenuOpenState(open);
    document.body.style.overflow = open ? "hidden" : "";
  };
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileLeistungenOpen, setIsMobileLeistungenOpen] = useState(false);
  const [isMobileAlternativenOpen, setIsMobileAlternativenOpen] = useState(false);
  const [authState, setAuthState] = useState<{ user: any; signOut: () => void } | null>(null);
  const dropdownTimeout = useRef<ReturnType<typeof setTimeout>>();
  const location = useLocation();
  const navigate = useNavigate();
  const isEnglish = i18n.language === "en";
  const isHome = location.pathname === "/" || location.pathname === "/en" || location.pathname === "/en/";
  // Gedockte Anfrageleiste zeigt bereits einen Anfrage-CTA – dann hier ausblenden.
  const dockedBarVisible = useDockedBarVisible();
  const { switchLanguage } = useLanguageSwitch();

  const LEISTUNGEN = [
    { label: t("nav.foodTransport"), to: isEnglish ? "/en/food-transport" : "/lebensmittel-transport", icon: Apple },
    { label: t("nav.pharmaTransport"), to: isEnglish ? "/en/pharma-transport" : "/pharma-transport", icon: Pill },
    { label: t("nav.cateringEvents"), to: isEnglish ? "/en/catering-events" : "/catering-events", icon: UtensilsCrossed },
    { label: t("nav.mobileCooling"), to: isEnglish ? "/en/mobile-cooling" : "/mobile-kuehlung", icon: Plug },
  ];

  const LEISTUNGEN_NEU = [
    { label: t("nav.kuehlwagenRental"), to: isEnglish ? "/en/refrigerated-van-hire" : "/kuehlwagen-mieten", icon: Snowflake },
    { label: t("nav.iceTransport"), to: isEnglish ? "/en/ice-transport" : "/eis-transport", icon: IceCream },
    { label: t("nav.fishTransport"), to: isEnglish ? "/en/fish-transport" : "/fisch-transport", icon: Fish },
    { label: t("nav.meatDonerTransport"), to: isEnglish ? "/en/meat-doner-transport" : "/fleisch-doener-transport", icon: Beef },
    { label: t("nav.buyRent"), to: isEnglish ? "/en/refrigerated-vans-for-sale" : "/kuehlwagen-kaufen", icon: ShoppingCart },
  ];

  // Only check auth if there's evidence of a session (localStorage token)
  useEffect(() => {
    const hasAuthToken = Object.keys(localStorage).some(k => k.includes("auth-token") || k.includes("supabase"));
    if (!hasAuthToken) return;

    import("@/integrations/supabase/client").then(({ supabase }) => {
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (session?.user) {
          setAuthState({
            user: session.user,
            signOut: () => supabase.auth.signOut().then(() => setAuthState(null)),
          });
        }
      });
    }).catch(() => {});
  }, []);

  useEffect(() => {
    let rafId: number;
    const handleScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        setIsScrolled(window.scrollY > 60);
      });
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      cancelAnimationFrame(rafId);
    };
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsMobileLeistungenOpen(false);
    setIsMobileAlternativenOpen(false);
  }, [location.pathname]);

  const scrollToSection = (id: string) => {
    setIsMobileMenuOpen(false);
    scrollToAnchor(id);
  };

  const handleDropdownEnter = () => {
    clearTimeout(dropdownTimeout.current);
    setIsDropdownOpen(true);
  };

  const handleDropdownLeave = () => {
    dropdownTimeout.current = setTimeout(() => setIsDropdownOpen(false), 150);
  };

  const transporterPath = isEnglish ? "/en/van-rental" : "/transporter-mieten";
  const minibusPath = isEnglish ? "/en/minibus-rental-frankfurt" : "/kleinbus-mieten-frankfurt";
  const aboutPath = isEnglish ? "/en/about-us" : "/ueber-uns";

  const anchorNav = (id: string, tracking: string, label: string) => {
    trackCTAClick(tracking, "header", label);
    setIsMobileMenuOpen(false);
    if (isHome) {
      scrollToAnchor(id);
    } else {
      navigate(isEnglish ? `/en/#${id}` : `/#${id}`);
    }
  };

  const NAV_ANCHORS: { id: string; label: string; tracking: string }[] = [
    { id: "fuhrpark", label: t("nav.vehicles"), tracking: "nav_fahrzeuge" },
    { id: "preise", label: t("nav.pricing"), tracking: "nav_preise" },
    { id: "technische-daten", label: t("nav.tech"), tracking: "nav_technik" },
    { id: "faq", label: t("nav.faq"), tracking: "nav_faq" },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 bg-card border-b border-border h-16 md:h-[72px] transition-shadow duration-300 ${
        isScrolled ? "shadow-md" : ""
      }`}
    >
      <div className="container mx-auto px-3 sm:px-4 md:px-6 h-full flex items-center justify-between gap-2 sm:gap-3 relative">
        <Link to={isEnglish ? "/en/" : "/"} className="flex-shrink-0">
          <span className="font-bold tracking-tight text-2xl sm:text-[26px]">
            <span className="text-primary">Frost</span>
            <span className="text-accent">flotte</span>
          </span>
        </Link>

        {/* Desktop Navigation – zentriert */}
        <nav className="hidden lg:flex 2xl:absolute 2xl:left-1/2 2xl:-translate-x-1/2 items-center gap-4 xl:gap-6 mx-auto">
          {NAV_ANCHORS.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => anchorNav(item.id, item.tracking, item.label)}
              className="text-sm font-medium hover:text-primary transition-colors whitespace-nowrap"
            >
              {item.label}
            </button>
          ))}

          {/* Mehr — Dropdown mit allen weiteren internen Links */}
          <div
            className="relative"
            onMouseEnter={handleDropdownEnter}
            onMouseLeave={handleDropdownLeave}
          >
            <button
              type="button"
              className="text-sm font-medium hover:text-primary transition-colors flex items-center gap-1 whitespace-nowrap"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              aria-expanded={isDropdownOpen}
            >
              {t("nav.more")}
              <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${isDropdownOpen ? "rotate-180" : ""}`} />
            </button>

            {isDropdownOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2">
                <div className="bg-card rounded-xl shadow-lg border border-border p-1.5 min-w-[260px] animate-in fade-in-0 slide-in-from-top-2 duration-200">
                  <div className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground px-3 pt-2 pb-1.5">
                    {t("nav.alternatives")}
                  </div>
                  <Link
                    to={transporterPath}
                    onClick={() => { trackCTAClick("nav_transporter", "header", t("nav.transporter")); setIsDropdownOpen(false); }}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm hover:bg-secondary/50 transition-colors"
                  >
                    <Truck className="h-4 w-4 text-primary flex-shrink-0" />
                    <span>{t("nav.transporter")}</span>
                  </Link>
                  <Link
                    to={minibusPath}
                    onClick={() => { trackCTAClick("nav_kleinbus", "header", t("nav.minibus")); setIsDropdownOpen(false); }}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm hover:bg-secondary/50 transition-colors"
                  >
                    <Truck className="h-4 w-4 text-primary flex-shrink-0" />
                    <span>{t("nav.minibus")}</span>
                  </Link>

                  <div className="my-1.5 h-px bg-border/70" role="separator" />
                  <div className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground px-3 pt-1 pb-1.5">
                    {t("nav.services")}
                  </div>
                  {[...LEISTUNGEN, ...LEISTUNGEN_NEU].map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm hover:bg-secondary/50 transition-colors"
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      <item.icon className="h-4 w-4 text-primary flex-shrink-0" />
                      <span>{item.label}</span>
                    </Link>
                  ))}

                  <div className="my-1.5 h-px bg-border/70" role="separator" />
                  <Link
                    to={aboutPath}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm hover:bg-secondary/50 transition-colors"
                    onClick={() => setIsDropdownOpen(false)}
                  >
                    <Truck className="h-4 w-4 text-primary flex-shrink-0" />
                    <span>{t("nav.about")}</span>
                  </Link>
                </div>
              </div>
            )}
          </div>
        </nav>

        <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3">
          {/* Desktop Telefon */}
          <a
            href={`tel:${PHONE_NUMBER}`}
            onClick={() => trackCTAClick("phone", "header", PHONE_DISPLAY)}
            className="hidden lg:flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-colors whitespace-nowrap"
          >
            <Phone className="h-4 w-4" strokeWidth={2.25} />
            {PHONE_DISPLAY}
          </a>

          {/* Sprachumschalter Desktop */}
          <button
            type="button"
            onClick={switchLanguage}
            className="hidden md:flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-primary transition-colors px-2 py-1 rounded-md hover:bg-secondary/50"
            aria-label={isEnglish ? "Switch to German" : "Switch to English"}
          >
            <span className={isEnglish ? "" : "text-foreground font-semibold"}>DE</span>
            <span className="text-muted-foreground/60">|</span>
            <span className={isEnglish ? "text-foreground font-semibold" : ""}>EN</span>
          </button>

          <Button
            onClick={() => {
              trackCTAClick("jetzt_mieten", "header", t("heroBookingBar.dockedCta"));
              if (isHome) { scrollToAnchor("fuhrpark"); } else { navigate(isEnglish ? "/en/" : "/"); setTimeout(() => scrollToAnchor("fuhrpark"), 300); }
            }}
            className={`hidden md:inline-flex h-11 px-5 rounded-[10px] bg-primary text-sm font-bold ${dockedBarVisible ? "md:hidden" : ""}`}
          >
            {t("heroBookingBar.dockedCta")}
          </Button>

          {/* Mobil: Telefon-Icon-Button */}
          <a
            href={`tel:${PHONE_NUMBER}`}
            onClick={() => trackCTAClick("phone", "header_mobile", PHONE_DISPLAY)}
            aria-label={t("header.call")}
            className="lg:hidden inline-flex items-center justify-center h-11 w-11 rounded-[10px] border border-border text-foreground hover:bg-secondary transition-colors"
          >
            <Phone className="h-4.5 w-4.5" strokeWidth={2.25} />
          </a>

          {/* Mobile Menu Toggle */}
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden h-11 w-11"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? t("header.closeMenu") : t("header.openMenu")}
          >
            {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>
      </div>


      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden absolute top-full left-0 right-0 bg-card border-t shadow-lg max-h-[calc(100dvh-4rem)] overflow-y-auto">
          <nav className="flex flex-col p-4 gap-1">
            {!isHome && (
              <Link
                to={isEnglish ? "/en/" : "/"}
                className="block py-2 px-3 hover:bg-secondary rounded-lg transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t("header.home")}
              </Link>
            )}

            {/* Anker-Links */}
            {NAV_ANCHORS.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => anchorNav(item.id, item.tracking, item.label)}
                className="text-left py-2 px-3 hover:bg-secondary rounded-lg transition-colors"
              >
                {item.label}
              </button>
            ))}


            {/* Alternativen — Akkordeon mit Transporter/Kleinbus */}
            <div>
              <button
                onClick={() => setIsMobileAlternativenOpen(!isMobileAlternativenOpen)}
                className="w-full flex items-center justify-between py-2 px-3 hover:bg-secondary rounded-lg transition-colors"
              >
                <span>{t("nav.alternatives")}</span>
                <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isMobileAlternativenOpen ? "rotate-180" : ""}`} />
              </button>
              {isMobileAlternativenOpen && (
                <div className="ml-4 flex flex-col gap-1 mt-1">
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground px-3 pt-2 pb-1">
                    {t("nav.alternativesHint")}
                  </div>
                  <Link
                    to={transporterPath}
                    className="flex items-center gap-2 py-2 px-3 text-sm text-muted-foreground hover:text-foreground hover:bg-secondary rounded-lg transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <Truck className="h-4 w-4 text-primary" />
                    {t("nav.transporter")}
                  </Link>
                  <Link
                    to={minibusPath}
                    className="flex items-center gap-2 py-2 px-3 text-sm text-muted-foreground hover:text-foreground hover:bg-secondary rounded-lg transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <Truck className="h-4 w-4 text-primary" />
                    {t("nav.minibus")}
                  </Link>
                </div>
              )}
            </div>

            {/* Leistungen accordion - always visible */}
            <div>
              <button
                onClick={() => setIsMobileLeistungenOpen(!isMobileLeistungenOpen)}
                className="w-full flex items-center justify-between py-2 px-3 hover:bg-secondary rounded-lg transition-colors"
              >
                <span>{t("nav.services")}</span>
                <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isMobileLeistungenOpen ? "rotate-180" : ""}`} />
              </button>
              {isMobileLeistungenOpen && (
                <div className="ml-4 flex flex-col gap-1 mt-1">
                  {LEISTUNGEN.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      className="flex items-center gap-2 py-2 px-3 text-sm text-muted-foreground hover:text-foreground hover:bg-secondary rounded-lg transition-colors"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <item.icon className="h-4 w-4 text-primary" />
                      {item.label}
                    </Link>
                  ))}
                  <div className="my-1 h-px bg-border/70" role="separator" />
                  {LEISTUNGEN_NEU.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      className="flex items-center gap-2 py-2 px-3 text-sm text-muted-foreground hover:text-foreground hover:bg-secondary rounded-lg transition-colors"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <item.icon className="h-4 w-4 text-primary" />
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to={aboutPath}
              className="block py-2 px-3 hover:bg-secondary rounded-lg transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {t("nav.about")}
            </Link>

            {/* Language Switcher Mobile */}
            <button
              type="button"
              onClick={() => { switchLanguage(); setIsMobileMenuOpen(false); }}
              className="w-full text-left py-2 px-3 hover:bg-secondary rounded-lg transition-colors flex items-center justify-between"
            >
              <span>{isEnglish ? "Deutsch" : "English"}</span>
              <span className="text-xs text-muted-foreground">{isEnglish ? "DE" : "EN"}</span>
            </button>

            {authState?.user && (
              <button
                onClick={() => {
                  authState.signOut();
                  setIsMobileMenuOpen(false);
                }}
                className="text-left py-2 px-3 hover:bg-secondary rounded-lg transition-colors flex items-center gap-2 text-destructive"
              >
                <LogOut className="h-4 w-4" />
                Abmelden
              </button>
            )}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
